'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { Slot } from '../slot';
import { ButtonGroupContext } from './context/button-group.context';
import { buttonGroupRecipe } from './styles/button-group.styles';
import type { ButtonGroupPropsInterface } from './types/button-group.types';

/**
 * Be sure to add an appropriate
 * [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) for assistive technologies.
 *
 * @related ['Button', 'LoadingButton', 'TextButton', 'ToggleButton']
 * @since 5.0.0
 */

export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupPropsInterface>(
  (props: ButtonGroupPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      tone,
      emphasis,
      conjoined = false,
      children,
      className,
      css: cssProp,
      fluid = false,
      reverse = false,
      orientation = 'horizontal',
      size,
      ...rest
    } = props;

    const responsiveFluid = splitResponsiveVariant('fluid', fluid);
    const responsiveReverse = splitResponsiveVariant('reverse', reverse);
    const responsiveOrientation = splitResponsiveVariant('orientation', orientation);

    const styles = css(
      buttonGroupRecipe.raw({
        conjoined,
        ...responsiveFluid,
        ...responsiveReverse,
        ...responsiveOrientation,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'div';

    const contextValue = useMemo(
      () => ({ tone, emphasis, fluid, size }),
      [tone, emphasis, fluid, size],
    );

    return (
      <ButtonGroupContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="ButtonGroup"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        >
          {children}
        </Component>
      </ButtonGroupContext.Provider>
    );
  },
);

ButtonGroup.displayName = 'ButtonGroup';
